import React from "react";

import { LoadingOverlay } from "../../components";
import { Button, Modal } from "../../components/ui";
import { AssignLookbook, CoverSettings } from "../../listings/molecules";
import { CopyToggles } from "./CopyToggles";
import { useRequestLookbook } from "./useRequestLookbook";
// The dark styling for CoverSettings / AssignLookbook lives here, scoped under
// `.rc-lb-wizard` (the listings wizard's wrapper). Reusing the components
// without that class left every field with the browser default — white boxes on
// the dark modal. Imported explicitly rather than relied on via the global
// bundle so the pairing survives any future code-splitting.
import "../../listings/templates/rc-upsert-lookbook.scss";

/**
 * Step two: the cover. Same fields as the listings lookbook cover (template,
 * name, publication month, assigned client) so the two flows produce covers that
 * are literally the same page — they share `CoverSettings` and `AssignLookbook`
 * rather than re-implementing them.
 *
 * GENERATE LOOKBOOK is a button, not an autosave, and stays one: rendering a PDF
 * is a discrete command like Publish or Send, not a field edit to persist.
 */
export const RequestCoverModal = () => {
  const {
    coverOpen,
    closeStep,
    backToAmounts,
    count,
    template,
    setTemplate,
    lookbookName,
    setLookbookName,
    lookbookDate,
    setLookbookDate,
    client,
    setClient,
    canGenerate,
    generate,
    submitting,
    error,
    isEditing,
  } = useRequestLookbook();

  const clientName = client
    ? `${client.first_name ?? ""} ${client.last_name ?? ""}`.trim() || client.email
    : null;

  const footer = (
    <>
      {/* Back, not close: the amounts entered on the previous step are saved on
          the request already, but losing the cover fields to get at them would
          still be a needless retype. */}
      <Button variant="secondary" onClick={backToAmounts}>
        Back
      </Button>
      <span className="rlb-modal__footer-hint">
        {canGenerate
          ? "The PDF is built in the background and appears under Lookbooks."
          : "Assign the lookbook to a client to continue."}
      </span>
      <Button
        size="lg"
        disabled={!canGenerate}
        loading={submitting}
        onClick={generate}
      >
        {isEditing ? "Regenerate Lookbook" : "Generate Lookbook"}
      </Button>
    </>
  );

  return (
    <Modal
      open={coverOpen}
      onClose={closeStep}
      variant="dialog"
      size="lg"
      title="Cover Page"
      description={`${count} ${count === 1 ? "request" : "requests"} in this lookbook`}
      footer={footer}
      className="rlb-modal"
    >
      {/* `rc-lb-wizard` is the scope every CoverSettings / AssignLookbook rule
          is written under — see the stylesheet import above. `rlb-cover`
          undoes the page-level padding and centring that class also carries. */}
      <div className="rc-lb-wizard rlb-cover">
        <CoverSettings
          {...{
            template,
            setTemplate,
            lookbookName,
            setLookbookName,
            lookbookDate,
            setLookbookDate,
          }}
        />

        <CopyToggles />

        {client ? (
          <div className="rlb-modal__client">
            <div className="rlb-modal__client-text">
              <span className="rlb-modal__client-label">Assigned to</span>
              <span className="rlb-modal__client-name">{clientName}</span>
              <span className="rlb-modal__client-email">{client.email}</span>
            </div>
            <Button variant="secondary" onClick={() => setClient(null)}>
              Change
            </Button>
          </div>
        ) : (
          <AssignLookbook onAssignClient={setClient} />
        )}
      </div>

      {error && <div className="rlb-modal__error">{error}</div>}

      {submitting && (
        <LoadingOverlay type="submitting" text="Generating lookbook..." />
      )}
    </Modal>
  );
};

export default RequestCoverModal;
